import { defineStore } from "pinia"
import {
  ApiCredentials,
  EvercamApiError,
  EvercamApiErrorCode,
  UnitSystem,
  User,
  UserFeatureFlag,
} from "@evercam/shared/types"
import md5 from "md5"
import { EvercamApi } from "@evercam/shared/api/evercamApi"
import { camelizeKeys } from "humps"
import { useLayoutStore } from "@evercam/dashboard/stores/layout"
import { useProjectStore } from "@evercam/dashboard/stores/project"
import { useCameraStore } from "@evercam/dashboard/stores/camera"
import axios from "@evercam/shared/api/client/axios"
import { AxiosError } from "axios"
import { useConnectorStore } from "@evercam/dashboard/stores/connector"
import { useNuxtApp, useRuntimeConfig } from "#app"

const isIframe = parent !== window
const urlParams = new URLSearchParams(window.location.search)
let statesKey = ""

if (urlParams.get("isWidget") || urlParams.get("is_widget")) {
  statesKey = "dashboardWidgetStates"
} else if (isIframe) {
  statesKey =
    (window.location.pathname.includes("widgets") && urlParams.get("camera")) ||
    urlParams.get("isWidget")
      ? "dashboardWidgetStates"
      : "dashboardDemoStates"
} else {
  statesKey = "dashboardAccountStates"
}

type ZohoLogin = { redirectToZoho: boolean }
type WidgetLogin = { widgetId: string; widgetKey: string }
type ImpersonationLogin = { impersonationToken: string }
type UsernamePasswordLogin = { username: string; password: string }

type LoginForm =
  | UsernamePasswordLogin
  | ApiCredentials
  | ImpersonationLogin
  | WidgetLogin
  | {}

export type AccountState = {
  widgetKey: string
  widgetId: string
  companyExid: string
  companyId: string
  companyName: string
  country: string
  email: string
  evercamAccount: string
  firstname: string
  idpHint: string
  isAdmin: boolean
  isApiLogin: boolean
  isBimCompareWidget: boolean
  isWidgetPreview: boolean
  isDemo: boolean
  isLiveViewWidget: boolean
  isUsingKeycloak: boolean
  isRecordingWidget: boolean
  isWidget: boolean
  lastname: string
  persona: string
  preventAccountInformationDialog: boolean
  redirectUrl: string
  telephone: string
  token: string
  unitSystem: UnitSystem
  features: string[]
} & ApiCredentials

//@ts-ignore
export const useAccountStore = defineStore("account", {
  state: (): AccountState => ({
    apiId: null,
    apiKey: null,
    widgetId: null,
    widgetKey: null,
    companyExid: null,
    companyId: null,
    isWidgetPreview: false,
    companyName: null,
    country: null,
    email: null,
    evercamAccount: "",
    firstname: null,
    idpHint: null,
    isAdmin: null,
    isApiLogin: false,
    isBimCompareWidget: false,
    isDemo: false,
    isLiveViewWidget: false,
    isUsingKeycloak: false,
    isRecordingWidget: false,
    isWidget: false,
    lastname: null,
    persona: null,
    preventAccountInformationDialog: false,
    telephone: null,
    redirectUrl: null,
    token: null,
    unitSystem: UnitSystem.Metric,
    features: [],
    userAccessChannel: null,
  }),
  actions: {
    async updateFeaturesAccess() {
      try {
        const { features } = await EvercamApi.users.fetchAccessFeatures(
          this.email
        )
        this.features = features
      } catch (error) {
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            "content.account.fetch_access_features_error"
          ),
          error,
        })
      }
    },
    hasUserAccessToFeature(feature: UserFeatureFlag) {
      return this.features?.includes(feature)
    },
    updateUser(user) {
      this.firstname = user?.firstname
      this.lastname = user?.lastname
      this.email = user?.email
      this.telephone = user?.telephone
      this.country = user?.country
      this.isAdmin = user?.isAdmin
      this.persona = user?.persona
      this.companyId = user?.companyId
      this.companyName = user?.companyName
      this.companyExid = user?.companyExid
      this.features = user?.features

      useNuxtApp().nuxt2Context.$errorTracker.setUser({
        email: user?.email,
        username: `${user?.firstname} ${user?.lastname}`,
      })
    },
    updateApiCredentials({ apiId, apiKey }) {
      this.apiId = apiId
      this.apiKey = apiKey
    },
    async logout({
      redirect = true,
      revoke = true,
      cleanState = true,
      persistCurrentUrl = true,
    } = {}) {
      if (revoke && this.isAuthorizedUser) {
        EvercamApi.users
          .logout(
            { token: this.token },
            { headers: { Authorization: `Bearer ${this.token}` } }
          )
          .catch((e) => {
            if (e instanceof AxiosError && e.response?.status !== 401) {
              useNuxtApp().nuxt2Context.$errorTracker?.save(e)
            }
          })
      }
      axios.setToken(null)
      useNuxtApp().nuxt2Context?.$posthog?.reset()

      this.isUsingKeycloak = false
      if (cleanState) {
        this.$reset()
        useCameraStore().$reset()
        useProjectStore().$reset()
        if (this.userAccessChannel) {
          this.userAccessChannel.leave()
          this.userAccessChannel = null
        }
      }

      this.updateApiCredentials({
        apiId: null,
        apiKey: null,
      })

      if (!redirect) {
        return
      }

      if (persistCurrentUrl) {
        this.redirectUrl = useNuxtApp().vue2App.$route?.fullPath
      }

      useNuxtApp().nuxt2Context.app.router.push("/v2/users/signin")
    },
    async login(
      { form, redirect = true }: { form?: LoginForm; redirect: boolean } = {
        redirect: true,
      }
    ) {
      const layoutStore = useLayoutStore()
      layoutStore.loader = true

      this.cleanUrlCredentials()

      const { route } = useNuxtApp().nuxt2Context,
        authCode = route.query.code,
        ssoProvider = window.localStorage?.getItem("ssoProvider"),
        { apiId, apiKey } = (form || {}) as ApiCredentials,
        { impersonationToken } = (form || {}) as ImpersonationLogin,
        { widgetKey, widgetId } = (form || {}) as WidgetLogin
      let response

      try {
        if (impersonationToken) {
          response = await EvercamApi.users.impersonationLogin(
            impersonationToken
          )
        } else if (authCode) {
          response = await EvercamApi.users.loginWithProvider(ssoProvider, {
            code: authCode as string,
            redirectUri: `${window.location.origin}`,
          })
        } else if (this.isApiLogin && apiId && apiKey) {
          response = await EvercamApi.users.loginWithApiCredentials(
            apiId,
            apiKey
          )
        } else if (widgetId && widgetKey) {
          response = await EvercamApi.users.loginWithWidgetCredentials(
            widgetId,
            widgetKey
          )
        } else {
          response = await EvercamApi.users.login(form as UsernamePasswordLogin)
        }
        const { token, evercamAccount, ...user } = response
        this.updateUser(user)
        this.evercamAccount = evercamAccount
        this.token = token
        axios.setToken(token)

        if ((!widgetKey || !widgetId) && (!apiId || !apiKey)) {
          EvercamApi.users
            .getCredentials()
            .then((apiCredentials) => this.updateApiCredentials(apiCredentials))
        }

        if ((form as ZohoLogin)?.redirectToZoho) {
          this.zohoLogin(user)
        } else if (redirect) {
          this.redirect()
        }
        useNuxtApp().nuxt2Context.$posthog.identify()
      } catch (e) {
        const error = e as EvercamApiError
        if (
          error.response?.data?.code === "INVALID_CREDENTIALS" ||
          error.response?.data?.code === "INVALID_TOKEN"
        ) {
          if (this.isApiLogin) {
            useNuxtApp().nuxt2Context.$notifications.warn("Invalid credentials")
          } else {
            useNuxtApp().nuxt2Context.$notifications.warn(
              error.response?.data?.message
            )
          }

          return
        }
        if (error?.response?.data?.code === EvercamApiErrorCode.PasswordReset) {
          useNuxtApp().nuxt2Context.app.router.push(
            `/v2/users/password-reset?email=${
              (form as UsernamePasswordLogin)?.username
            }`
          )
        }
        if (
          error?.response?.data?.code === 502 ||
          error?.response?.data?.code === 503
        ) {
          useNuxtApp().nuxt2Context.$notifications.error({
            text: useNuxtApp().vue2App.$i18n.t(
              "content.auth.server_down"
            ) as string,
          })

          return
        }
        layoutStore.loader = false

        const sourceUrlRequestedSso = window.localStorage?.getItem(
          "sourceUrlRequestedSso"
        )
        const errorMessage =
          sourceUrlRequestedSso === "/v2/users/signup"
            ? "signup_failed"
            : "login_failed"
        useNuxtApp().nuxt2Context.$notifications.error({
          text: useNuxtApp().vue2App.$i18n.t(
            `content.auth.${errorMessage}`
          ) as string,
          error,
          showDetails: ssoProvider
            ? error?.response?.data?.code ===
              EvercamApiErrorCode.RequireShareRequest
            : true,
        })

        if (ssoProvider) {
          return new Promise((_, reject) => reject())
        }
      } finally {
        layoutStore.loader = false
      }
    },
    async apiLogin({ apiId, apiKey }) {
      this.cleanUrlCredentials()
      if (this.token) {
        await this.logout({ redirect: false })
      }
      this.updateApiCredentials({ apiId, apiKey })
      this.isApiLogin = true
      await this.login({
        form: {
          apiId,
          apiKey,
        },
        redirect: false,
      })

      useNuxtApp().nuxt2Context.$errorTracker?.leaveBreadcrumb({
        category: "info",
        message: `Remote login. With token? : ${!!this.token}`,
      })
    },
    async widgetLogin({ widgetKey, widgetId }) {
      this.cleanUrlCredentials()
      if (this.token) {
        await this.logout({ redirect: false })
      }

      this.widgetId = widgetId
      this.widgetKey = widgetKey

      await this.login({
        form: {
          widgetKey,
          widgetId,
        },
        redirect: false,
      })

      useNuxtApp().nuxt2Context.$errorTracker?.leaveBreadcrumb({
        category: "info",
        message: `Remote login. With token? : ${!!this.token}`,
      })
    },
    async impersonationLogin() {
      const { route } = useNuxtApp().nuxt2Context
      const { impersonationToken } = camelizeKeys(route.query) as {
        impersonationToken: string
      }
      this.cleanUrlCredentials()
      if (this.token) {
        await this.logout({ redirect: false })
      }
      await this.login({ form: { impersonationToken }, redirect: false })
    },
    zohoLogin({ email, lastname, firstname }, redirect = "_self") {
      const timestamp = useNuxtApp()
        .nuxt2Context.$moment.utc()
        .valueOf() as unknown as string
      const remoteAuthKey = `${useRuntimeConfig().remoteAuthKey}`,
        operation = "signup",
        utype = "portal",
        fullName = `${firstname} ${lastname}`,
        loginName = fullName.toLowerCase().replace(/ /g, "."),
        apikey = md5(
          `${operation}${email}${loginName}${fullName}${utype}${remoteAuthKey}${timestamp}`
        )
      const redirectURL = `https://support.evercam.io/support/RemoteAuth?operation=${encodeURI(
        operation
      )}&email=${encodeURI(email)}&fullname=${encodeURI(
        fullName
      )}&loginname=${encodeURI(loginName)}&utype=${encodeURI(
        utype
      )}&ts=${encodeURI(timestamp)}&apikey=${apikey}`
      window.open(redirectURL, redirect)
    },
    zohoLogout() {
      this.$reset()
      useCameraStore().$reset()
      useProjectStore().$reset()
      axios.setToken(null)
    },
    async fetchUserData() {
      try {
        if (!this.token) {
          return
        }
        const jwt = JSON.parse(atob(this.token.split(".")[1]))
        const response = await EvercamApi.users.show(jwt.email)
        const userData = response?.users[0]

        this.updateUser(userData)
        this.evercamAccount = userData?.evercamAccount
      } catch (e) {
        const error = e as EvercamApiError
        if (error.response?.status !== 401) {
          useNuxtApp().nuxt2Context.$errorTracker?.save(error)
        }
      }
    },
    async fetchUserCredentials() {
      const credentials = await EvercamApi.users.getCredentials()
      this.updateApiCredentials(credentials)
    },
    cleanUrlCredentials() {
      const { route } = useNuxtApp().nuxt2Context
      route.query.api_id = null
      route.query.api_key = null
      route.query.authorization = null
      route.query.impersonation_token = null
      const query = document.location.search.replace(
        /((&)?api_id=[a-zA-Z0-9]+|(&)?api_key=[a-zA-Z0-9]+|(&)?authorization=[\w-]+\.[\w-]+\.[\w-]+|(&)?impersonation_token=[a-z0-9-]+)/g,
        ""
      )
      window.history.replaceState(
        {},
        document.title,
        `${document.location.pathname}${query.replace(/\?$/g, "")}`
      )
    },
    redirect() {
      const redirectUrl = this.redirectUrl
      this.redirectUrl = null
      const authPaths = [
        "/v2/users/signin",
        "/v2/users/signout",
        "/v2/users/signup",
      ]
      const finalUrl =
        redirectUrl && !authPaths.some((path) => redirectUrl.includes(path))
          ? redirectUrl
          : "/v2/projects"

      if (useNuxtApp().nuxt2Context?.route?.query?.code) {
        useNuxtApp().nuxt2Context.app.router.push(finalUrl)
      } else {
        window.location.href = finalUrl
      }
    },
    async handle401Error(error: AxiosError) {
      const url = error.config?.url

      const connectorStore = useConnectorStore()
      if (connectorStore.isConnectionError(error)) {
        return connectorStore.handle401Error(error)
      }

      const shouldLogout = !this.isWidget && url.includes(axios.env.baseUrl)
      if (shouldLogout) {
        await this.logout({ revoke: false })
      }
    },
    async initUserAccessRestrictionSocket(callback) {
      if (this.isUserOffline || !this.email) {
        return
      }
      if (this.userAccessChannel) {
        return
      }
      try {
        this.userAccessChannel = useNuxtApp().nuxt2Context.$websocket.channel(
          useNuxtApp().nuxt2Context.$config.public.socketURL,
          `user:${this.email}:restriction_updates`
        )
        this.userAccessChannel.on("user_restriction_changed", (payload) => {
          const emailKey = Object.keys(payload.user)[0]
          if (emailKey === this.email) {
            callback()
          }
        })
        this.userAccessChannel.join().receive("error", (err) => {
          console.error("Failed to join channel:", err)
          this.userAccessChannel = null
        })
      } catch (error) {
        this.userAccessChannel = null
      }
    },
  },
  getters: {
    fullname: (state): string => `${state.firstname} ${state.lastname}`,
    isUserOffline: (state): boolean => !state.email,
    tokenMetaData(): { impersonated_by?: string } {
      const isTokenExpired = useNuxtApp().nuxt2Context.$auth.isTokenExpired()
      if (this.token && !isTokenExpired) {
        const tokenData = this.token.split(".")[1]

        return JSON.parse(atob(tokenData))
      }

      return {}
    },
    isImpersonationLogin(): boolean {
      return !!this.tokenMetaData.impersonated_by
    },
    impersonatorEmail(): string {
      return this.tokenMetaData.impersonated_by
    },
    isAuthorizedUser(): boolean {
      return (
        this.token &&
        this.email &&
        !useNuxtApp().nuxt2Context?.$auth?.isTokenExpired()
      )
    },
    userObject(): User {
      return {
        firstname: this.firstname,
        lastname: this.lastname,
        email: this.email,
        telephone: this.telephone,
        country: this.country,
        isAdmin: this.isAdmin,
        persona: this.persona,
        companyId: this.companyId,
        companyName: this.companyName,
        companyExid: this.companyExid,
        features: this.features,
      }
    },
  },
  persist: {
    key: statesKey,
  },
})
